import React, { type AriaRole, type ReactNode, useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import { faTimes } from 'lib/font_awesome/light';

import { Icon } from './components/icon';

import * as styles from './alert.module.css';

/** Visual variant for {@link Alert}. */
export enum AlertType {
  INFO = 'info',
  FEATURE = 'feature',
  WARNING = 'warning',
  ERROR = 'error',
}

export type AlertProps = {
  /** Call-to-action. `{ url, content }` renders an anchor; any other `ReactNode` renders as-is. */
  action?:
    | {
        url: string;
        content: ReactNode;
      }
    | ReactNode;
  /** `target` for the anchor form of `action`. */
  actionTarget?: '_self' | '_blank' | '_parent' | '_top';
  /** Dismiss after 5s. Requires `onDismiss`. @defaultValue false */
  autoDismiss?: boolean;
  /** Passed back to `onDismiss`. Useful when one handler manages many alerts. */
  id?: string;
  message: string | ReactNode;
  /** Drop the default bottom margin (use in flex/grid layouts). @defaultValue false */
  noMargin?: boolean;
  /** Drop the colored left accent (use when nested in a card/drawer). No effect on `feature`. @defaultValue false */
  removeBorderLeft?: boolean;
  role?: AriaRole;
  title?: string | ReactNode;
  type: 'info' | 'feature' | 'warning' | 'error';
  /** Renders the close button and is called on click or auto-dismiss. Receives `id` if provided. */
  onDismiss?: (id?: string) => void;
};

// oxlint-disable-next-line local-rules/max-props
export function Alert({
  action,
  actionTarget,
  autoDismiss = false,
  id = undefined,
  message,
  noMargin = false,
  removeBorderLeft = false,
  role = undefined,
  title = undefined,
  type,
  onDismiss = undefined,
}: AlertProps) {
  function handleDismiss() {
    if (!onDismiss) return;
    onDismiss(id);
  }

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (autoDismiss && onDismiss) {
      timeout = setTimeout(() => onDismiss(id), 5_000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [autoDismiss, onDismiss, id, type]);

  return (
    <div
      className={classNames(styles.alert, {
        [styles.info]: type === 'info',
        [styles.feature]: type === 'feature',
        [styles.warning]: type === 'warning',
        [styles.error]: type === 'error',
        [styles.removeBorderLeft]: removeBorderLeft,
        [styles.noMargin]: noMargin,
      })}
      role={role}
    >
      <div>
        <div>
          <Icon type={type} />

          <div>
            {title && <div className={styles.title}>{title}</div>}
            <div className={styles.message}>{message}</div>
          </div>
        </div>

        {action &&
          (typeof action === 'object' && 'url' in action ? (
            <a
              className={styles.primaryAction}
              href={action.url}
              rel="noopener noreferrer"
              target={actionTarget}
            >
              {action.content}
            </a>
          ) : (
            action
          ))}
      </div>

      {onDismiss && (
        <button
          aria-label={`close ${type} alert`}
          className={styles.close}
          type="button"
          onClick={handleDismiss}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );
}
